// src/components/family/ChildFilters.tsx
// Search and filter controls for children list

import { useState, useEffect, useRef } from 'react';
import {
  Search,
  Filter,
  Grid3X3,
  List,
  ChevronDown,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import type { ChildFilters as ChildFiltersType } from '@/features/children/types/childTypes';

// Mock diagnoses for filter - in production, fetch from API
const DIAGNOSIS_OPTIONS = [
  { value: '', label: 'All Diagnoses' },
  { value: 'cerebral_palsy', label: 'Cerebral Palsy' },
  { value: 'hie', label: 'HIE' },
  { value: 'erbs_palsy', label: "Erb's Palsy" },
  { value: 'kernicterus', label: 'Kernicterus' },
  { value: 'spina_bifida', label: 'Spina Bifida' },
  { value: 'other', label: 'Other' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'age', label: 'Age (Youngest)' },
  { value: 'age_desc', label: 'Age (Oldest)' },
  { value: 'created_at', label: 'Recently Added' },
  { value: 'updated_at', label: 'Recently Updated' },
];

interface ChildFiltersProps {
  filters: ChildFiltersType;
  onFiltersChange: (filters: ChildFiltersType) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  totalCount?: number;
  filteredCount?: number;
}

export function ChildFilters({
  filters,
  onFiltersChange,
  viewMode,
  onViewModeChange,
  totalCount = 0,
  filteredCount,
}: ChildFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (searchValue !== filters.search) {
        onFiltersChange({ ...filters, search: searchValue || undefined });
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDiagnosisChange = (value: string) => {
    onFiltersChange({
      ...filters,
      diagnosis_id: value || undefined,
    });
  };

  const handleSortChange = (value: string) => {
    const [sort_by, order] = value.includes('_desc')
      ? [value.replace('_desc', ''), 'desc']
      : [value, 'asc'];
    
    onFiltersChange({
      ...filters,
      sort_by: sort_by as ChildFiltersType['sort_by'],
      sort_order: order as 'asc' | 'desc',
    });
    setSortDropdownOpen(false);
  };

  const handleAgeRangeChange = (min?: number, max?: number) => {
    onFiltersChange({
      ...filters,
      age_min: min,
      age_max: max,
    });
  };

  const clearFilters = () => {
    setSearchValue('');
    onFiltersChange({});
    setShowAdvanced(false);
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.diagnosis_id ||
    filters.age_min ||
    filters.age_max
  );

  const currentSortValue = filters.sort_by
    ? filters.sort_order === 'desc'
      ? `${filters.sort_by}_desc`
      : filters.sort_by
    : 'name';

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Name (A-Z)';

  return (
    <div className="space-y-4">
      {/* Main Controls Row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full pl-11 pr-10 py-3 rounded-xl bg-white border border-warm-gray/10 focus:border-sage focus:ring-2 focus:ring-sage/10 outline-none transition-all text-navy placeholder:text-warm-gray"
          />
          {searchValue && (
            <button
              onClick={() => setSearchValue('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-cream transition-colors"
            >
              <X className="w-4 h-4 text-warm-gray" />
            </button>
          )}
        </div>

        {/* Diagnosis Filter */}
        <select
          value={filters.diagnosis_id || ''}
          onChange={(e) => handleDiagnosisChange(e.target.value)}
          className="px-4 py-3 rounded-xl bg-white border border-warm-gray/10 focus:border-sage focus:ring-2 focus:ring-sage/10 outline-none transition-all text-navy cursor-pointer min-w-[160px]"
        >
          {DIAGNOSIS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Sort Dropdown */}
        <div ref={sortRef} className="relative">
          <button
            onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-warm-gray/10 hover:border-sage/30 transition-all text-navy"
          >
            <SlidersHorizontal className="w-4 h-4 text-warm-gray" />
            <span className="text-sm">{currentSortLabel}</span>
            <ChevronDown className={`w-4 h-4 text-warm-gray transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {sortDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-medium border border-warm-gray/10 py-2 z-20">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSortChange(option.value)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-cream transition-colors ${
                    currentSortValue === option.value ? 'text-sage font-medium bg-sage/5' : 'text-navy'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
            showAdvanced
              ? 'bg-sage text-white border-sage'
              : 'bg-white border-warm-gray/10 text-navy hover:border-sage/30'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filters</span>
          {hasActiveFilters && !showAdvanced && (
            <span className="w-2 h-2 rounded-full bg-terracotta" />
          )}
        </button>

        {/* View Mode Toggle */}
        <div className="flex items-center bg-white border border-warm-gray/10 rounded-xl p-1">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'grid'
                ? 'bg-sage text-white'
                : 'text-warm-gray hover:text-navy hover:bg-cream'
            }`}
            title="Grid view"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'list'
                ? 'bg-sage text-white'
                : 'text-warm-gray hover:text-navy hover:bg-cream'
            }`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div className="p-4 bg-cream/50 rounded-xl border border-warm-gray/10 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-navy">Advanced Filters</h4>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-sage hover:text-sage-dark transition-colors"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium text-navy-light mb-2">
                Age Range (years)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  min={0}
                  max={25}
                  value={filters.age_min || ''}
                  onChange={(e) =>
                    handleAgeRangeChange(
                      e.target.value ? Number(e.target.value) : undefined,
                      filters.age_max
                    )
                  }
                  className="w-full px-3 py-2 rounded-lg bg-white border border-warm-gray/10 focus:border-sage focus:ring-1 focus:ring-sage/10 outline-none text-sm"
                />
                <span className="text-warm-gray">—</span>
                <input
                  type="number"
                  placeholder="Max"
                  min={0}
                  max={25}
                  value={filters.age_max || ''}
                  onChange={(e) =>
                    handleAgeRangeChange(
                      filters.age_min,
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  className="w-full px-3 py-2 rounded-lg bg-white border border-warm-gray/10 focus:border-sage focus:ring-1 focus:ring-sage/10 outline-none text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      {(filteredCount !== undefined || totalCount > 0) && (
        <div className="flex items-center justify-between text-sm text-warm-gray">
          <span>
            {filteredCount !== undefined && filteredCount !== totalCount ? (
              <>
                Showing <span className="font-medium text-navy">{filteredCount}</span> of{' '}
                <span className="font-medium text-navy">{totalCount}</span> children
              </>
            ) : (
              <>
                <span className="font-medium text-navy">{totalCount}</span>{' '}
                {totalCount === 1 ? 'child' : 'children'}
              </>
            )}
          </span>
          
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sage hover:text-sage-dark transition-colors"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ChildFilters;

